import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useLocale } from '../i18n/LocaleContext';

// App-backend usage this month (managed data / server): what the plan includes, what was used,
// and the customer's explicit, capped approval of extra usage. Hidden when the platform does
// not run app backends yet.
const STATE_KEYS = { ok: 'stateOk', warning: 'stateWarning', needs_approval: 'stateNeedsApproval', insufficient_credits: 'stateInsufficient' };

function format(value) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export default function ResourceUsagePanel() {
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [cap, setCap] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.resourceUsage().then(res => {
      setData(res);
      setCap(String(res?.usage?.approvedCapCredits ?? 0));
    }).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data?.enabled || !data.usage) return null;
  const usage = data.usage;

  const approve = async (event) => {
    event.preventDefault();
    setSaving(true); setStatus('');
    try {
      const res = await api.approveResourceUsage(Number(cap));
      setData(current => ({ ...current, usage: res.usage }));
      setStatus(t('resourceUsage.approveSaved'));
    } catch (err) {
      setStatus(err.response?.data?.error || t('resourceUsage.approveFailed'));
    } finally { setSaving(false); }
  };

  return (
    <div className="account-section" data-testid="resource-usage">
      <div className="section-heading">{t('resourceUsage.title')}</div>
      <div className="account-card">
        <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '0.75rem' }}>{t('resourceUsage.intro')}</p>
        <div style={{ fontSize: '13px', marginBottom: '0.75rem' }}>{t('resourceUsage.period', { period: usage.period })}</div>
        {Object.entries(usage.metrics || {}).map(([key, metric]) => (
          <div key={key} style={{ marginBottom: '0.6rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13.5px' }}>
              <span>{metric.label}</span>
              <span>{t('resourceUsage.used', { used: format(metric.used), included: `${format(metric.included)}${metric.measureUnit ? ` ${metric.measureUnit}` : ''}` })}</span>
            </div>
            <div role="progressbar" aria-label={metric.label} aria-valuenow={Math.min(metric.percent, 100)} aria-valuemin={0} aria-valuemax={100}
              style={{ height: 6, borderRadius: 3, background: 'var(--border-color, #e5e7eb)', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(metric.percent, 100)}%`, height: '100%', background: metric.percent >= 100 ? '#b91c1c' : metric.percent >= 80 ? '#d97706' : '#16a34a' }} />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>{t('resourceUsage.price', { price: metric.price, unit: metric.unit })}</small>
          </div>
        ))}
        <p role="status" style={{ fontSize: '13.5px', margin: '0.75rem 0', fontWeight: usage.paused ? 600 : 400 }}>{t(`resourceUsage.${STATE_KEYS[usage.state] || 'stateOk'}`)}</p>
        {usage.overageCredits > 0 && <p style={{ fontSize: '13px' }}>{t('resourceUsage.overage', { credits: usage.overageCredits, charged: usage.chargedCredits })}</p>}
        {usage.approvedCapCredits > 0 && <p style={{ fontSize: '13px' }}>{t('resourceUsage.approved', { cap: usage.approvedCapCredits })}</p>}
        {usage.maxApprovalCredits > 0 && (
          <form onSubmit={approve} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="resource-usage-cap" style={{ fontSize: '13px' }}>{t('resourceUsage.approveLabel')}</label>
            <input id="resource-usage-cap" type="number" min={usage.chargedCredits || 0} max={usage.maxApprovalCredits} step={1} value={cap} onChange={e => setCap(e.target.value)} style={{ width: 110 }} />
            <button type="submit" className="btn" disabled={saving || cap === '' || Number(cap) === usage.approvedCapCredits}>{t('resourceUsage.approveButton')}</button>
          </form>
        )}
        {status && <small role="status" style={{ display: 'block', marginTop: '0.5rem' }}>{status}</small>}
        <p style={{ color: 'var(--text-secondary)', fontSize: '12.5px', marginTop: '0.75rem' }}>{t('resourceUsage.productionNote')}</p>
        {!usage.apps?.length && <p style={{ color: 'var(--text-secondary)', fontSize: '12.5px' }}>{t('resourceUsage.noApps')}</p>}
      </div>
    </div>
  );
}
